import NameCode from '@/component/name-code';
import PaginationTable, {ColumnType} from '@/component/pagination-table';
import {HoldState} from '@/model/const';
import PageData from '@/types/page-data';
import PageParam from '@/types/page-param';
import HoldUtil from '@/util/hold-util';
import LabelUtil from '@/util/label-util';
import Utils from '@/util/utils';
import {InfoCircleOutlined} from '@ant-design/icons';
import {Tooltip} from 'antd';
import React from 'react';
import {NavigateFunction, useNavigate} from 'react-router';
import {ReportData, ReportHoldData, ReportRecommendData} from '../types';
import './report-table.scss';

interface ReportTableProps {
    data?: PageData<ReportData>;
    expandedRowKeys?: {recommend: number[]; hold: number[]};
    onRowExpand?: (key: 'recommend' | 'hold', expanded: boolean, record: ReportData) => void;
    onStarClick?: (record: ReportData, star: number) => void;
    setPageParam?: (param: PageParam) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getColumnData = (navigate: NavigateFunction, onStarClick?: (record: ReportData, star: number) => void): ColumnType<ReportData>[] => {
    return [
        {
            title: '名称代码',
            dataIndex: 'name',
            key: 'name',
            render: (_text: string, record: ReportData) => (
                <NameCode
                    name={record.name}
                    code={record.code}
                    color={record.tagColor}
                    expireTime={record.expireTime}
                    star={record.tagStar}
                    onStarClick={(star) => onStarClick?.(record, star)}
                />
            ),
        },
        {
            title: '所属行业',
            dataIndex: 'industry',
            key: 'industry',
        },
        {
            title: '推荐状态',
            dataIndex: 'recommendState',
            key: 'recommendState',
            render: (_text: string, record: ReportData) => {
                let state = LabelUtil.getRecommendState(record.recommendState);
                if (!state && Utils.isArrayHasData(record.recommend)) {
                    state = '--';
                }
                return state;
            },
        },
        {
            title: '持有状态',
            dataIndex: 'holdState',
            key: 'holdState',
            render: (_text: string, record: ReportData) => {
                let state = LabelUtil.getHoldState(record.holdState);
                if (!state && Utils.isArrayHasData(record.hold)) {
                    state = '--';
                }
                return state;
            },
        },
        {
            title: '现价',
            dataIndex: 'latestPrice',
            key: 'latestPrice',
            render: (text: number, record: ReportData) => {
                const price = Utils.formatPrice(text !== -1 ? text : undefined);
                const _price = () => {
                    return <div className="latest-price">{price}</div>;
                };
                if (text <= 0) return _price();
                if (record.holdState !== HoldState.BOUGHT) return _price();
                if (!Utils.isArrayHasData(record.hold)) return _price();
                const holds = record.hold?.filter((item) => item.holdState === HoldState.BOUGHT) || [];
                const hold = holds.length > 1 ? holds.reduce((a, b) => (a.buyPrice < b.buyPrice ? a : b)) : holds[0];
                if (!hold) return _price();
                const rate = ((text - hold.buyPrice) / hold.buyPrice) * 100;
                return (
                    <div className="latest-price-wrap">
                        <div className="latest-price">{price}</div>
                        <span className={`latest-price-rate ${rate < 0 ? 'negative' : 'positive'}`}>{Utils.formatPercentage(rate)}</span>
                    </div>
                );
            },
        },
        {
            title: '保本价',
            dataIndex: 'breakevenPrice',
            key: 'breakevenPrice',
            render: (text: number, record: ReportData) => (
                <div className="breakeven-price">
                    {Number(text) > 0 ? Utils.formatPrice(text) : '--'}
                    {record.nearbyInterestDate && (
                        <Tooltip title={`近期付息：${Utils.formatDate(record.nearbyInterestDate)}`}>
                            <InfoCircleOutlined className="info" />
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            width: 120,
            render: (text: number) => {
                if (!text) return '';
                return (
                    <div className="tip-text-wrap">
                        <Tooltip title={<div className="tip-text">{text}</div>}>
                            <div className="content">{text}</div>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];
};

const ReportTable: React.FC<ReportTableProps> = (props) => {
    const {data, expandedRowKeys, onRowExpand, onStarClick, setPageParam} = props;
    const navigate = useNavigate();

    const renderRecommendExpanded = (report: ReportData) => {
        const getRecommendColumn = (): ColumnType<ReportRecommendData>[] => {
            return [
                {
                    title: '状态',
                    dataIndex: 'recommendState',
                    key: 'recommendState',
                    width: 60,
                    render: (text: string) => LabelUtil.getRecommendState(text),
                },
                {
                    title: '买入价',
                    dataIndex: 'recommendBuyPrice',
                    key: 'recommendBuyPrice',
                    render: (text: number) => Utils.formatPrice(text),
                },
                {
                    title: '保本价',
                    dataIndex: 'breakevenPrice',
                    key: 'breakevenPrice',
                    render: (text: number) => Utils.formatPrice(text),
                },
                {
                    title: '溢价率',
                    dataIndex: 'premiumRate',
                    key: 'premiumRate',
                    render: (text: number) => Utils.formatPercentage(text),
                },
                {
                    title: '年限',
                    dataIndex: 'remainYear',
                    key: 'remainYear',
                    render: (text: number) => text || 0,
                },
                {
                    title: '规模',
                    dataIndex: 'residualScale',
                    key: 'residualScale',
                    render: (text: number) => `${text || 0}亿元`,
                },
                {
                    title: '总市值',
                    dataIndex: 'totalMarketValue',
                    key: 'totalMarketValue',
                    render: (text: number) => `${text || 0}亿元`,
                },
                {
                    title: '到期收益',
                    dataIndex: 'yieldToMaturity',
                    key: 'yieldToMaturity',
                    render: (text: number) => Utils.formatPercentage(text),
                },
                {
                    title: '卖出收益',
                    dataIndex: 'profitRate',
                    key: 'profitRate',
                    render: (text: number) => Utils.formatPercentage(text),
                },
                {
                    title: '买入时间',
                    dataIndex: 'recommendBuyTime',
                    key: 'recommendBuyTime',
                    render: (text: number) => Utils.formatDate(text),
                },
                {
                    title: '卖出时间',
                    dataIndex: 'recommendSellTime',
                    key: 'recommendSellTime',
                    render: (text: number) => Utils.formatDate(text),
                },
                {
                    title: '买入',
                    dataIndex: 'recommendBuyReason',
                    key: 'recommendBuyReason',
                    width: 60,
                    render: (text: number) => {
                        if (!text) return '';
                        return (
                            <div className="tip-text-wrap">
                                <Tooltip title={<div className="tip-text">{text}</div>}>
                                    <div className="content" style={{maxWidth: '60px'}}>
                                        {text}
                                    </div>
                                </Tooltip>
                            </div>
                        );
                    },
                },
                {
                    title: '卖出',
                    dataIndex: 'recommendSellReason',
                    key: 'recommendSellReason',
                    width: 60,
                    render: (text: number) => {
                        if (!text) return '';
                        return (
                            <div className="tip-text-wrap">
                                <Tooltip title={<div className="tip-text">{text}</div>}>
                                    <div className="content" style={{maxWidth: '60px'}}>
                                        {text}
                                    </div>
                                </Tooltip>
                            </div>
                        );
                    },
                },
            ];
        };

        if (!Utils.isArrayHasData(report.recommend)) {
            return <div />;
        }

        return (
            <div className="expanded-row">
                <div className="label-wrap label-recommend">
                    <div className="label">荐</div>
                </div>
                <div className="expanded-recommend">
                    <PaginationTable
                        columns={getRecommendColumn()}
                        data={{
                            page: 0,
                            size: (report.recommend || []).length,
                            total: (report.recommend || []).length,
                            content: report.recommend || [],
                        }}
                        showPagination={false}
                    />
                </div>
            </div>
        );
    };

    const renderHoldExpanded = (report: ReportData) => {
        const getHoldColumn = (): ColumnType<ReportHoldData>[] => {
            return [
                {
                    title: '持有状态',
                    dataIndex: 'holdState',
                    key: 'holdState',
                    render: (text: string) => LabelUtil.getHoldState(text),
                },
                {
                    title: '数量',
                    dataIndex: 'quantity',
                    key: 'quantity',
                },
                {
                    title: '买入时间',
                    dataIndex: 'buyTime',
                    key: 'buyTime',
                    render: (text: number) => Utils.formatDate(text),
                },
                {
                    title: '买入价',
                    dataIndex: 'buyPrice',
                    key: 'buyPrice',
                    render: (text: number) => Utils.formatPrice(text),
                },
                {
                    title: '卖出时间',
                    dataIndex: 'sellTime',
                    key: 'sellTime',
                    render: (text: number, record: ReportHoldData) => (record.holdState === HoldState.SOLD ? Utils.formatDate(text) : '--'),
                },
                {
                    title: '卖出价',
                    dataIndex: 'sellPrice',
                    key: 'sellPrice',
                    render: (text: number, record: ReportHoldData) => (record.holdState === HoldState.SOLD ? Utils.formatPrice(text) : '--'),
                },
                {
                    title: '收益',
                    dataIndex: '',
                    key: 'profit',
                    render: (_text: number, record: ReportHoldData) => HoldUtil.getProfit(record, report),
                },
                {
                    title: '收益率',
                    dataIndex: '',
                    key: 'profitRate',
                    render: (_text: number, record: ReportHoldData) => HoldUtil.getProfitRate(record, report),
                },
                {
                    title: '买入',
                    dataIndex: 'buyDescription',
                    key: 'buyDescription',
                    width: 60,
                    render: (text: number) => {
                        if (!text) return '';
                        return (
                            <div className="tip-text-wrap">
                                <Tooltip title={<div className="tip-text">{text}</div>}>
                                    <div className="content">{text}</div>
                                </Tooltip>
                            </div>
                        );
                    },
                },
                {
                    title: '卖出',
                    dataIndex: 'sellDescription',
                    key: 'sellDescription',
                    width: 60,
                    render: (text: number) => {
                        if (!text) return '';
                        return (
                            <div className="tip-text-wrap">
                                <Tooltip title={<div className="tip-text">{text}</div>}>
                                    <div className="content">{text}</div>
                                </Tooltip>
                            </div>
                        );
                    },
                },
            ];
        };

        if (!Utils.isArrayHasData(report.hold)) {
            return <div />;
        }

        return (
            <div className="expanded-row">
                <div className="label-wrap label-hold">
                    <div className="label">持</div>
                </div>
                <div className="expanded-hold">
                    <PaginationTable
                        columns={getHoldColumn()}
                        data={{
                            page: 0,
                            size: (report.hold || []).length,
                            total: (report.hold || []).length,
                            content: report.hold || [],
                        }}
                        showPagination={false}
                    />
                </div>
            </div>
        );
    };

    const renderExpandedRow = (report: ReportData) => {
        return (
            <div className="expanded-wrap">
                {renderRecommendExpanded(report)}
                {renderHoldExpanded(report)}
            </div>
        );
    };

    return (
        <div className="report-table">
            <PaginationTable
                columns={getColumnData(navigate, onStarClick)}
                data={data}
                setPageParam={setPageParam}
                expandable={{
                    expandedRowRender: renderExpandedRow,
                    expandedRowKeys: expandedRowKeys?.hold || [],
                    onExpand: (expanded, record) => onRowExpand?.('hold', expanded, record),
                    rowExpandable: (record: ReportData) => Utils.isArrayHasData(record.recommend) || Utils.isArrayHasData(record.hold),
                }}
            />
        </div>
    );
};

export default ReportTable;
